import { useEffect, useState } from 'react';
import type { MediaSignal } from '@tsr/shared-types';
import { findHazardLexiconEntry } from '@tsr/shared-types';
import {
  signalBodyExcerpt,
  signalDisplayTitle,
  signalSourceLabel,
  signalTime,
} from './lib/mediaSignalFeed';

/**
 * Pinned media-signal tile (#65/#183 wave 3b) — what a kind:'news' pin renders
 * when its id is a WIRE SIGNAL (a media_signals uuid) rather than a curated
 * outlet slug (see NewsMedia for the branch). A signal is an article / post,
 * not a live stream, so the tile is an HONEST static post card — provenance
 * chrome + headline + link out — never a fake video frame. Ambient: no red, no
 * motion (Law 4 / Law 6).
 */
export function SignalMedia({ id, label }: { id: string; label: string }): JSX.Element {
  const [signal, setSignal] = useState<MediaSignal | null>(null);
  const [failed, setFailed] = useState(false);
  // #361 thumbnail — hidden if the upstream image fails to load.
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setSignal(null);
    setFailed(false);
    void fetch(`/api/media-signals/${encodeURIComponent(id)}`)
      .then((r) => (r.ok ? (r.json() as Promise<MediaSignal>) : null))
      .then((s) => {
        if (cancelled) return;
        if (s) setSignal(s);
        else setFailed(true);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (failed) {
    // The wire's retention sweep (or a bad id) — say so instead of spinning.
    return (
      <div className="flex h-full w-full items-center justify-center p-3">
        <p className="text-[11px] text-slate-500">
          Signal no longer on the wire — “{label}”.
        </p>
      </div>
    );
  }
  if (!signal) {
    return <p className="p-3 text-[11px] text-slate-500">Loading signal…</p>;
  }

  const body = signalBodyExcerpt(signal, 280);
  return (
    <div
      data-testid="signal-media"
      className="flex h-full w-full min-w-0 flex-col justify-center gap-1.5 overflow-hidden p-3"
    >
      <div className="flex items-center gap-1.5">
        <span className="shrink-0 rounded bg-slate-800 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-300">
          {signalSourceLabel(signal.source)}
        </span>
        {signal.language !== 'und' && (
          <span className="shrink-0 rounded bg-slate-800 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-slate-400">
            {signal.language}
          </span>
        )}
        <span
          className="min-w-0 truncate text-[10px] text-slate-500"
          title={new Date(signalTime(signal)).toLocaleString()}
        >
          {new Date(signalTime(signal)).toLocaleString()}
        </span>
      </div>
      <p className="text-sm font-medium leading-snug text-slate-100">
        {signalDisplayTitle(signal)}
      </p>
      {signal.media && !imgFailed && (
        <div
          data-testid="signal-media-thumb"
          data-media-kind={signal.media.kind}
          className="relative overflow-hidden rounded border border-slate-700/60"
        >
          <img
            src={signal.media.url}
            alt={signalDisplayTitle(signal)}
            loading="lazy"
            decoding="async"
            referrerPolicy="no-referrer"
            onError={() => setImgFailed(true)}
            className="max-h-28 w-full object-cover"
          />
          {signal.media.kind === 'video' && (
            <span
              aria-hidden
              className="pointer-events-none absolute inset-0 flex items-center justify-center text-2xl text-white/90 drop-shadow-[0_1px_3px_rgba(0,0,0,0.8)]"
            >
              ▶
            </span>
          )}
        </div>
      )}
      {body && <p className="text-[11px] leading-snug text-slate-400">{body}</p>}
      <div className="flex flex-wrap items-center gap-1">
        {signal.geoName && (
          <span className="text-[10px] text-slate-500">📍 {signal.geoName}</span>
        )}
        {signal.hazardKeywords.map((k) => (
          <span
            key={k}
            className="rounded-full border border-slate-600/80 px-1.5 py-px text-[10px] text-slate-300"
          >
            {findHazardLexiconEntry(k)?.label ?? k}
          </span>
        ))}
        {signal.url && (
          <a
            href={signal.url}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-auto text-[11px] text-sky-400 hover:text-sky-300"
          >
            open source ↗
          </a>
        )}
      </div>
    </div>
  );
}
